/**
 * Helpers for reading the `/configurator` gamification catalog
 * (`configuratorConfig.gamificationItems`) — shared by the Product pages.
 */
import type { ArtBundle, DefaultItem, GamificationTier, OperatingRegion, TextTranslations } from '@/components/modules/spark/configurator/types';
import type { GamificationItemWithTiers } from '@/store/slices/configurator-config';
import type { ShowcaseHeader, ShowcaseHighlight } from '../mock-data/types';

/** Fallback language for catalog translations and the active-language default. */
export const DEFAULT_LANG = 'en';

/** Fallback currency (3-letter code) when the brand's operating region carries none. The
 *  configurator wizard falls back to USD too, so the Product pages match it. */
export const DEFAULT_CURRENCY = 'USD';

/** Operating region the market benchmark falls back to when the active brand carries
 *  no `operating_region` (the field is not emitted by /casino-instance/list yet). */
export const DEFAULT_REGION = 'INT';

/** The brand's billing currency: the `currency_code` of the catalog operating region
 *  matching `regionCode` (the brand's `operating_region`, falling back to {@link DEFAULT_REGION}).
 *  Mirrors the configurator wizard, which prices each tier from the selected region's
 *  currency (`region_data.currency_code`). Falls back to {@link DEFAULT_CURRENCY} when the
 *  region is unknown or carries no currency. */
export const currencyForRegion = (
  regions: OperatingRegion[] | undefined,
  regionCode: string | undefined,
): string => {
  const region = regions?.find((entry) => entry.code === regionCode);
  return (
    region?.region_data?.currency_code ??
    region?.currency?.currency_code ??
    region?.currency_code ??
    DEFAULT_CURRENCY
  );
};

/** The off tier (priority 0) is a real ladder rung whose selection disables the feature. */
export const isOffTier = (tier: GamificationTier): boolean => tier.priority === 0;

/** The item's enabled-tier ladder: every tier except the off one, priority-sorted upstream. */
export const enabledLadder = (catalogItem: GamificationItemWithTiers): GamificationTier[] =>
  catalogItem.tiers.filter((tier) => !isOffTier(tier));

/** Resolve a TextTranslations record for the active language (then 'en', then any entry).
 *  Some catalog entries carry a flat string here despite the type — pass it through
 *  as-is (Object.values on a string would split it into characters). */
export const pickTranslation = (text: TextTranslations | string | undefined, lang: string): string | undefined => {
  if (!text) {
    return undefined;
  }
  if (typeof text === 'string') {
    return text;
  }
  const value = text[lang] ?? text[DEFAULT_LANG] ?? Object.values(text)[0];
  return value || undefined;
};

/** Display text from an art bundle's translation fields; undefined when none is set. */
export const artBundleText = (bundle: ArtBundle | undefined, lang: string): string | undefined =>
  pickTranslation(bundle?.text_name, lang) ??
  pickTranslation(bundle?.properties?.name, lang) ??
  pickTranslation(bundle?.properties?.title, lang);

/** Tier display label: art-bundle translations first, falling back to the flat catalog tier
 *  name minus the "<item name> " prefix ("AirTimer pro" → "pro"). */
export const tierLabelFor = (tier: GamificationTier, itemName: string, lang: string): string => {
  const translated = artBundleText(tier.art_bundle?.[0], lang);
  if (translated) {
    return translated;
  }
  const prefix = `${itemName} `;
  return tier.name.toLowerCase().startsWith(prefix.toLowerCase()) ? tier.name.slice(prefix.length) : tier.name;
};

/** Longest prefix shared by every name, cut back to the last word boundary so it can never end
 *  mid-token: "Wheel Plus" and "Wheel Pro" share the characters "Wheel P", but the tier token
 *  starts at "Plus". Empty for fewer than two names (nothing to compare). */
const sharedNamePrefix = (names: string[]): string => {
  if (names.length < 2) {
    return '';
  }
  const [first, ...rest] = names;
  let end = 0;
  while (end < first.length && rest.every((name) => name[end] === first[end])) {
    end += 1;
  }
  const raw = first.slice(0, end);
  return raw.slice(0, raw.lastIndexOf(' ') + 1);
};

/** Drop a leading `prefix` (case-insensitive) plus whatever separator it leaves behind. */
const withoutPrefix = (name: string, prefix: string): string =>
  (prefix && name.toLowerCase().startsWith(prefix.toLowerCase()) ? name.slice(prefix.length) : name)
    .replace(/^[\s\-–—:_.]+/, '')
    .trim();

/** Tier token for a tier PILL — the short form ("Ultra"). Deliberately NOT the art-bundle display
 *  name that {@link tierLabelFor} prefers: that one is the full marketing name ("META JACKPOT -
 *  ULTRA") and this pill nests inside a bar as narrow as 180px, where it would swallow the track.
 *
 *  Catalog tier names do carry a per-item prefix, but an INCONSISTENT one: sometimes the item's own
 *  name ("Calendar Ultra"), sometimes a short form of it ("Wheel Ultra" for the item "Wheel of
 *  Fortune"), sometimes a marketing one ("Meta-Jackpot Ultra" for "Jackpot"). Matching the item name
 *  alone therefore left some pills long and others short. So three strips are tried — the item name,
 *  the prefix the item's OWN tier names all share, and a trailing " - " segment — and the shortest
 *  result wins: that is the part which actually distinguishes this tier from its siblings. Falls
 *  back to the full display label if every strip comes up empty. */
export const tierShortLabelFor = (tier: GamificationTier, item: GamificationItemWithTiers, lang: string): string => {
  const candidates = [
    withoutPrefix(tier.name, `${item.name} `),
    withoutPrefix(tier.name, sharedNamePrefix(item.tiers.map((entry) => entry.name))),
    tier.name.split(' - ').pop()?.trim() ?? '',
  ].filter(Boolean);
  const shortest = candidates.reduce((best, next) => (next.length < best.length ? next : best), tier.name);
  return shortest || tierLabelFor(tier, item.name, lang);
};

/** Coerce a `video_duration_s` value (string from /configurator) to a number; undefined
 *  on absent / blank / non-numeric, so the showcase autoplay falls back to the text pace. */
const toDurationSeconds = (raw: string | number | undefined): number | undefined => {
  if (raw === undefined || raw === null || raw === '') {
    return undefined;
  }
  const value = Number(raw);
  return Number.isNaN(value) ? undefined : value;
};

/** The showcase talking points for one tier: every `section_id: 'highlight'` art bundle,
 *  in catalog order, mapped to {@link ShowcaseHighlight} (title + description, plus the
 *  per-highlight demo clip from `video_url`). Bundles with neither title nor text are
 *  dropped. Feature-tier highlights carry no source pills (that machinery is the Players
 *  step's), so this is title + typed description + optional video only. */
export const tierShowcaseHighlights = (tier: GamificationTier, lang: string): ShowcaseHighlight[] =>
  (tier.art_bundle ?? [])
    .filter((bundle) => bundle.properties?.section_id === 'highlight')
    .map((bundle, index): ShowcaseHighlight => {
      const props = bundle.properties;
      const videoUrl = props?.video_url;
      return {
        id: `${tier.id}-h${index}`,
        title: pickTranslation(props?.title, lang) ?? '',
        text: pickTranslation(props?.description, lang) ?? '',
        video: videoUrl ? { src: videoUrl, loop: true } : undefined,
        videoDurationS: toDurationSeconds(props?.video_duration_s),
      };
    })
    .filter((highlight) => highlight.title !== '' || highlight.text !== '');

/** The showcase heading for one tier, from its `section_id: 'highlightsHeader'` art bundle
 *  (`title` → eyebrow, e.g. "The hype is real"; `description` → title, e.g. "How it
 *  works?"). Undefined when the tier carries no such bundle, so callers fall back. */
export const tierShowcaseHeader = (tier: GamificationTier, lang: string): ShowcaseHeader | undefined => {
  const bundle = (tier.art_bundle ?? []).find(
    (entry) => entry.properties?.section_id === 'highlightsHeader',
  );
  if (!bundle) {
    return undefined;
  }
  return {
    eyebrow: pickTranslation(bundle.properties?.title, lang) ?? '',
    title: pickTranslation(bundle.properties?.description, lang) ?? '',
  };
};

/** Position (0-100) of the "Market" indicator on a feature's You-vs-Market bar for the
 *  deployed region: the `feature_level` of the item's art bundle whose `country_code`
 *  matches `regionCode`. /configurator sends `feature_level` as a string (e.g. "60"), so
 *  it is coerced. Returns undefined when no region is known or no matching bundle carries a
 *  numeric `feature_level`, so callers can fall back. The value is already a 0-100 bar
 *  position (not a ladder level); clamped defensively. */
export const marketLevelForRegion = (
  catalogItem: GamificationItemWithTiers,
  regionCode: string | undefined,
): number | undefined => {
  if (!regionCode) {
    return undefined;
  }
  const bundle = catalogItem.art_bundle?.find((ab) => ab.properties?.country_code === regionCode);
  const raw = bundle?.properties?.feature_level;
  if (raw === undefined || raw === null || raw === '') {
    return undefined;
  }
  const level = Number(raw);
  if (Number.isNaN(level)) {
    return undefined;
  }
  return Math.max(0, Math.min(100, Math.round(level)));
};

/** Placeholder "Market" position (0-100) when no per-region `market_level` exists yet:
 *  the middle of the item's enabled-tier ladder. */
export const fallbackMarketLevel = (maxLevel: number): number =>
  maxLevel > 0 ? Math.round((Math.ceil(maxLevel / 2) / maxLevel) * 100) : 0;

/** The three competitiveness axes. On a tier these are the authored RELATIVE weights
 *  (`score_*`); once normalized against the live budget ({@link coverageTotals}) they are
 *  0-100 gauge values. */
export type FeatureScores = { market: number; profitability: number; stickiness: number };

const ZERO_SCORES: FeatureScores = { market: 0, profitability: 0, stickiness: 0 };

/** Coerce a `score_*` art-bundle value (string from /configurator) to a non-negative weight;
 *  0 on absent / blank / non-numeric ("n/a") / negative (the documented fallback, so a
 *  misconfigured entry can never subtract from a score). */
const toScore = (raw: string | number | undefined): number => {
  if (raw === undefined || raw === null || raw === '') {
    return 0;
  }
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : 0;
};

/** Competitiveness scores of a tier, read from its art bundle carrying `score_market` /
 *  `score_profitability` / `score_stickiness`. No such bundle (or no tier) → all zeroes. */
export const tierScores = (tier: GamificationTier | undefined): FeatureScores => {
  const bundle = tier?.art_bundle?.find(
    (ab) =>
      ab.properties?.score_market !== undefined ||
      ab.properties?.score_profitability !== undefined ||
      ab.properties?.score_stickiness !== undefined,
  );
  if (!bundle) {
    return ZERO_SCORES;
  }
  return {
    market: toScore(bundle.properties?.score_market),
    profitability: toScore(bundle.properties?.score_profitability),
    stickiness: toScore(bundle.properties?.score_stickiness),
  };
};

// --- The scoring set ------------------------------------------------------------
// The catalog ships rungs that must never define a feature's top / max: RETIRED tiers
// (`is_active: false`, kept so an owner who still holds one resolves, but not purchasable),
// tiers authored without usable scores (no bundle, or 0 / blank / "n/a" on every axis: a
// data gap), and platform-DISABLED items (spec: they hold authored weights but sit outside
// the live budget). The display ladder ({@link enabledLadder}) keeps them; the SCORING
// ladder below excludes them. Reading the top off the display ladder is what produced
// negative "to unlock" deltas (a retired, unscored Ultra read as a 0 top under a scored Pro).

/** Whether a tier carries at least one usable (finite, > 0) score. A tier that is 0 / blank /
 *  "n/a" on EVERY axis, or has no score bundle at all, is unscored: it can neither define an
 *  item's top nor credit an owner. A zero on SOME axes only is a legitimate zero weight there
 *  and keeps the tier in. */
export const hasUsableScores = (tier: GamificationTier | undefined): boolean => {
  const scores = tierScores(tier);
  return scores.market > 0 || scores.profitability > 0 || scores.stickiness > 0;
};

/** Catalog `is_active` on an item: a platform-disabled item contributes nothing to any axis
 *  and is not offered as a missing feature (the API contract asks consumers to filter on it). */
export const isActiveItem = (catalogItem: Pick<GamificationItemWithTiers, 'is_active'>): boolean =>
  catalogItem.is_active !== false;

/** Catalog `is_active` on a tier: a retired rung (false) is not purchasable, so it never
 *  defines an item's top / max, even though it stays in the display ladder. */
export const isActiveTier = (tier: GamificationTier): boolean => tier.is_active !== false;

/** The SCORING ladder: the enabled (non-off) tiers that are active AND carry usable scores,
 *  priority-sorted upstream. This, not {@link enabledLadder}, defines an item's top and max. */
export const scoringLadder = (catalogItem: GamificationItemWithTiers): GamificationTier[] =>
  enabledLadder(catalogItem).filter((tier) => isActiveTier(tier) && hasUsableScores(tier));

/** The item's top scoring tier: its max, the 100% reference for the item. Undefined when no
 *  tier qualifies (then the item contributes nothing to any budget). */
export const topScoringTier = (catalogItem: GamificationItemWithTiers): GamificationTier | undefined => {
  const ladder = scoringLadder(catalogItem);
  return ladder[ladder.length - 1];
};

/** The ladder the Product pages SHOW for an item, in priority order: its purchasable rungs
 *  (non-off, catalog-active, on a catalog-active ITEM) plus the tier the brand owns and the tier
 *  a pending invoice pays for, even when those two are retired or the item is platform-disabled.
 *  A rung nobody holds that the mask does not offer is not shown and never counts toward a
 *  card's level / max; a disabled item therefore offers nothing beyond what the brand already
 *  holds. Shared by the product root cards and the product-detail picker so the two pages agree
 *  on a feature's position. */
export const visibleTiers = (
  catalogItem: GamificationItemWithTiers,
  ownedTierId?: number,
  pendingTierId?: number,
): GamificationTier[] => {
  const offered = isActiveItem(catalogItem);
  return enabledLadder(catalogItem).filter(
    (tier) => (offered && isActiveTier(tier)) || tier.id === ownedTierId || tier.id === pendingTierId,
  );
};

/** Component-wise sum of two FeatureScores. */
const addScores = (a: FeatureScores, b: FeatureScores): FeatureScores => ({
  market: a.market + b.market,
  profitability: a.profitability + b.profitability,
  stickiness: a.stickiness + b.stickiness,
});

/** Component-wise difference `a - b`. */
const subtractScores = (a: FeatureScores, b: FeatureScores): FeatureScores => ({
  market: a.market - b.market,
  profitability: a.profitability - b.profitability,
  stickiness: a.stickiness - b.stickiness,
});

/** Component-wise minimum. */
const minScores = (a: FeatureScores, b: FeatureScores): FeatureScores => ({
  market: Math.min(a.market, b.market),
  profitability: Math.min(a.profitability, b.profitability),
  stickiness: Math.min(a.stickiness, b.stickiness),
});

/** The weights an owner is CREDITED for holding `ownedTier` on `catalogItem`: the tier's own
 *  scores when usable, otherwise the highest scoring rung at or below it (a retired or
 *  misconfigured owned tier still counts as at least the level beneath it: activation always
 *  pays), capped per axis at the item's top scoring tier (the spec's min() rule, so a retired
 *  rung that outscores what is purchasable today never pushes an item above its own max). All
 *  zero when the item has no scoring tier at all. */
const creditedTierScores = (catalogItem: GamificationItemWithTiers, ownedTier: GamificationTier): FeatureScores => {
  const top = tierScores(topScoringTier(catalogItem));
  const basis = hasUsableScores(ownedTier)
    ? ownedTier
    : scoringLadder(catalogItem)
        .filter((tier) => tier.priority <= ownedTier.priority)
        .pop();
  return minScores(tierScores(basis), top);
};

/** A feature's "unlock" benefit deltas in RAW weights: the item's top SCORING tier minus the
 *  owner's credit at `currentTier` ({@link creditedTierScores}). Never negative by
 *  construction. `currentTier` undefined (a not-enabled feature) counts as zero, so the delta
 *  is the full top value. A platform-disabled item is all zero. Convert to points of the axis
 *  budget with {@link normalizeScores} before display. */
export const scoreDeltasToTop = (
  catalogItem: GamificationItemWithTiers,
  currentTier: GamificationTier | undefined,
): FeatureScores => {
  if (!isActiveItem(catalogItem)) {
    return { ...ZERO_SCORES };
  }
  const top = tierScores(topScoringTier(catalogItem));
  const current = currentTier ? creditedTierScores(catalogItem, currentTier) : ZERO_SCORES;
  return subtractScores(top, current);
};

/** The weights an enabled purchase is credited with ({@link creditedTierScores}). Null when
 *  the purchase is not counted at all: the catalog item is missing or platform-disabled
 *  (contributes nothing), or the purchase sits on the off / an unknown tier (not enabled, the
 *  same {@link enabledLadder} test `featureFromApiItem` applies). */
export const enabledItemScores = (
  item: Pick<DefaultItem, 'item_id' | 'tier_id'>,
  catalog: GamificationItemWithTiers[],
): FeatureScores | null => {
  const catalogItem = catalog.find((entry) => entry.id === item.item_id);
  if (!catalogItem || !isActiveItem(catalogItem)) {
    return null;
  }
  const tier = enabledLadder(catalogItem).find((entry) => entry.id === item.tier_id);
  return tier ? creditedTierScores(catalogItem, tier) : null;
};

/** The raw NUMERATOR of each axis: the credited weights ({@link enabledItemScores}) summed
 *  across the brand's enabled features. Not-counted purchases contribute nothing. */
export const sumEnabledScores = (
  items: ReadonlyArray<Pick<DefaultItem, 'item_id' | 'tier_id'>>,
  catalog: GamificationItemWithTiers[],
): FeatureScores =>
  items.reduce<FeatureScores>((totals, item) => {
    const scores = enabledItemScores(item, catalog);
    return scores ? addScores(totals, scores) : totals;
  }, { ...ZERO_SCORES });

/** Weights of the three coverages in the overall Product Score (spec: Market Coverage 0.40,
 *  Retention 0.30, Monetization 0.30). Axis naming: `market` is the spec's Market Weight,
 *  `stickiness` its Retention, `profitability` its Monetization. */
export const SCORE_WEIGHTS: FeatureScores = { market: 0.4, profitability: 0.3, stickiness: 0.3 };

/** The overall Product Score: the weighted blend of the three coverages (market 40%,
 *  stickiness + profitability 30% each), rounded UP to the next integer. */
export const overallScore = (totals: FeatureScores): number =>
  Math.ceil(
    SCORE_WEIGHTS.market * totals.market +
      SCORE_WEIGHTS.profitability * totals.profitability +
      SCORE_WEIGHTS.stickiness * totals.stickiness,
  );

// --- New-website calibration ------------------------------------------------
// A brand-new website has barely any real signal, so its raw competitiveness
// totals would read "Poor" from day one. Instead we let it launch strong and
// settle into reality: each axis starts lifted to a {@link CALIBRATION_FLOOR}
// floor and decays LINEARLY back to its true value over the
// {@link CALIBRATION_WINDOW_DAYS}-day calibration window. The lift only ever
// raises a value toward the floor (an axis already above it is left alone), so
// the overall score is "at least the floor" on launch day and never higher than
// the truth once calibration ends. The Product page surfaces a "Calibrating…"
// note while {@link isCalibrating} holds.

/** Days over which the launch lift decays from full to nothing. */
export const CALIBRATION_WINDOW_DAYS = 5;
/** The score floor a fresh website is lifted toward on launch day. */
export const CALIBRATION_FLOOR = 90;

/** Share of the launch lift still in effect at `daysSinceLaunch`: 1 on launch day,
 *  decaying linearly to 0 at (and after) {@link CALIBRATION_WINDOW_DAYS}. A NaN/negative
 *  age is treated as fully past the window (factor 0), so an unknown launch date never
 *  inflates the score. */
export const calibrationFactor = (daysSinceLaunch: number): number => {
  if (!Number.isFinite(daysSinceLaunch)) {
    return 0;
  }
  return Math.max(0, 1 - Math.max(0, daysSinceLaunch) / CALIBRATION_WINDOW_DAYS);
};

/** Whether the launch calibration is still lifting the score (i.e. the website is
 *  younger than the {@link CALIBRATION_WINDOW_DAYS}-day window). Drives the
 *  Product page's "Calibrating…" note. */
export const isCalibrating = (daysSinceLaunch: number): boolean => calibrationFactor(daysSinceLaunch) > 0;

/** Lift one axis toward {@link CALIBRATION_FLOOR} by the current factor; an axis already
 *  at/above the floor is never lowered. */
const calibrateAxis = (value: number, factor: number): number =>
  value + Math.max(0, CALIBRATION_FLOOR - value) * factor;

/** Apply the launch calibration to the three competitiveness totals for a website of the
 *  given age, rounding each axis for display. On launch day every axis reads at least the
 *  floor; at/after the window the totals are returned unchanged (rounded). The overall
 *  score is derived from these, so the headline gauge and the sub-gauges always agree. */
export const calibrateTotals = (totals: FeatureScores, daysSinceLaunch: number): FeatureScores => {
  const factor = calibrationFactor(daysSinceLaunch);
  if (factor <= 0) {
    // Coverage is a normalized ratio (fractional); round it for the gauges like the lifted branch.
    return {
      market: Math.round(totals.market),
      profitability: Math.round(totals.profitability),
      stickiness: Math.round(totals.stickiness),
    };
  }
  return {
    market: Math.round(calibrateAxis(totals.market, factor)),
    profitability: Math.round(calibrateAxis(totals.profitability, factor)),
    stickiness: Math.round(calibrateAxis(totals.stickiness, factor)),
  };
};

/** The raw DENOMINATOR of each axis, the live scoring budget: every ACTIVE catalog item's top
 *  scoring tier summed per axis, i.e. "everyone at max" across the enabled set (spec §4; the
 *  player threshold is deferred, so every enabled item counts). Disabled items and retired /
 *  unscored rungs are excluded. Normalizing against this ({@link coverageTotals}) makes each
 *  axis a 0-100 budget BY CONSTRUCTION, whatever absolute values the catalog authors, and
 *  re-budgets automatically when an item is added, retired or disabled. */
export const axisMaxTotals = (catalog: GamificationItemWithTiers[]): FeatureScores =>
  catalog
    .filter(isActiveItem)
    .reduce<FeatureScores>((acc, item) => addScores(acc, tierScores(topScoringTier(item))), { ...ZERO_SCORES });

/** Every axis at its full budget: the max a normalized axis (and the gauges) can reach. */
export const FULL_COVERAGE: FeatureScores = { market: 100, profitability: 100, stickiness: 100 };

/** Express raw weights as points of each axis's 100 budget: `raw / max × 100` per axis, and 0
 *  (never NaN) where the budget is empty (catalog not loaded, or no scored tier on that axis). */
export const normalizeScores = (raw: FeatureScores, maxTotals: FeatureScores): FeatureScores => {
  const axis = (value: number, max: number): number => (max > 0 ? (value / max) * 100 : 0);
  return {
    market: axis(raw.market, maxTotals.market),
    profitability: axis(raw.profitability, maxTotals.profitability),
    stickiness: axis(raw.stickiness, maxTotals.stickiness),
  };
};

/** The three coverages (spec §4: Retention = `stickiness`, Monetization = `profitability`, plus
 *  the Market Weight share as `market`): the brand's credited weights over the live budget, per
 *  axis, on 0-100. Unrounded ({@link calibrateTotals} rounds for display). Reads 100 when every
 *  active item sits on its top scoring tier, and is capped at 100 by construction because each
 *  credit is at most its item's max ({@link enabledItemScores}). */
export const coverageTotals = (
  items: ReadonlyArray<Pick<DefaultItem, 'item_id' | 'tier_id'>>,
  catalog: GamificationItemWithTiers[],
): FeatureScores => normalizeScores(sumEnabledScores(items, catalog), axisMaxTotals(catalog));

/** Active, purchasable (non-off, `is_active`) tiers of active items that carry NO usable scores:
 *  a data-authoring gap. Such a rung silently drops out of the scoring set (the rung below
 *  becomes the item's top), so the UI stays plausible while the budget is wrong. Surfaced as a
 *  dev-mode warning by `useProductScore` so it gets fixed at the source. */
export const unscoredActiveTiers = (
  catalog: GamificationItemWithTiers[],
): { item: GamificationItemWithTiers; tier: GamificationTier }[] =>
  catalog.filter(isActiveItem).flatMap((item) =>
    enabledLadder(item)
      .filter((tier) => isActiveTier(tier) && !hasUsableScores(tier))
      .map((tier) => ({ item, tier })),
  );

/** Per-axis multiplier for the feature cards' "to unlock" deltas during launch calibration.
 *  The raw deltas sum to `(max - current)` per axis; while calibration lifts the current totals
 *  toward the floor, the remaining headroom shrinks to `(max - calibratedCurrent)`, so the cards
 *  are scaled by that ratio to keep the panel score and the remaining deltas summing to `max`
 *  ({@link FULL_COVERAGE}, 100 per axis). The factor is 1 (cards unchanged) once calibration
 *  ends or when an axis is already maxed (no headroom). Clamped to [0, 1]. */
export const calibratedRemainingScale = (
  baselineTotals: FeatureScores,
  maxTotals: FeatureScores,
  daysSinceLaunch: number,
): FeatureScores => {
  const factor = calibrationFactor(daysSinceLaunch);
  const axisScale = (current: number, max: number): number => {
    const baseHeadroom = max - current;
    if (factor <= 0 || baseHeadroom <= 0) {
      return 1;
    }
    const calibratedHeadroom = max - calibrateAxis(current, factor);
    return Math.max(0, Math.min(1, calibratedHeadroom / baseHeadroom));
  };
  return {
    market: axisScale(baselineTotals.market, maxTotals.market),
    profitability: axisScale(baselineTotals.profitability, maxTotals.profitability),
    stickiness: axisScale(baselineTotals.stickiness, maxTotals.stickiness),
  };
};

/** Scale a feature card's raw "to unlock" deltas by the per-axis calibration multiplier
 *  ({@link calibratedRemainingScale}), rounding each for display. Identity at/after the window. */
export const scaleDeltas = (deltas: FeatureScores, scale: FeatureScores): FeatureScores => ({
  market: Math.round(deltas.market * scale.market),
  profitability: Math.round(deltas.profitability * scale.profitability),
  stickiness: Math.round(deltas.stickiness * scale.stickiness),
});